import { setOtherServices } from 'feature/claims/claimSlice';
import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { TableRow, Typography } from 'styles';
import palette from 'styles/theme/Palette';
import {
	IClaim,
	IClaimComponents,
	IValidationError,
	SERVICES_TYPE,
} from 'types/Claim';
import { useTranslation } from 'utils/translations';
import { formatInputNumberValue, serviceIdToLabel } from 'utils/utils';
import { OutlinedInput, TableCell } from './PriceEstimate';

const StandardItemSection: FC<{
	error: {
		grossAmount: boolean;
		maxValue: boolean;
		retailNotTaxAndNet: boolean;
		taxBiggerNet: boolean;
	};
	tempAmounts: IClaim['amount'];
	setTempAmounts: Dispatch<SetStateAction<IClaim['amount']>>;
	checkIsError: (
		row: IClaimComponents,
		forceChecks?: boolean,
	) => {
		grossAmount: boolean;
		maxValue: boolean;
		retailNotTaxAndNet: boolean;
		taxBiggerNet: boolean;
	};
	handleChangeAmounts: (
		formattedValue: string,
		serviceId: number | null,
		key: string,
	) => void;
	isOtherComponent: (row: IClaimComponents) => boolean;
	addCurrency: (value: number | string | undefined) => string;
	status: 'beforeCalculation' | 'afterCalculation' | 'onEdit';
	showGross?: boolean;
	validationErrors: IValidationError[];
	showTaxColumns: boolean;
}> = ({
	tempAmounts,
	setTempAmounts,
	checkIsError,
	handleChangeAmounts,
	isOtherComponent,
	addCurrency,
	status,
	showGross,
	validationErrors,
	showTaxColumns,
}) => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const dispatch = useDispatch<AppDispatch>();

	const { member } = useSelector((state: RootState) => state.member);

	useEffect(() => {
		const otherServices =
			member?.benefit?.others.map(el => ({
				serviceLabel: el.serviceLabel,
				serviceId: el.serviceId,
			})) || [];

		dispatch(setOtherServices(otherServices));
	}, [dispatch, member]);

	const checkIsErrorBenefitId = (id: number) => {
		return (
			validationErrors.filter(err => {
				const error = Number(err.field);
				return (
					error === id ||
					(error === SERVICES_TYPE.FRAME_AND_LENSES &&
						id === SERVICES_TYPE.LENSES_ONLY) ||
					(error === SERVICES_TYPE.FRAME_AND_LENSES &&
						id === SERVICES_TYPE.FRAME_ONLY)
				);
			}).length > 0
		);
	};

	return (
		<>
			{tempAmounts.components
				.filter(el => !isOtherComponent(el))
				.map(row => (
					<TableRow
						key={row.serviceId}
						sx={{
							height: { sm: 56, lg: 70 },
							backgroundColor: palette.white.main,
							outline: `2px solid ${palette.grey6.main}`,
						}}
					>
						<TableCell sx={{ width: { sm: 136, lg: 230 } }}>
							<Typography variant="body" sx={{ pl: '10px' }}>
								{tClientLabels(serviceIdToLabel(row.serviceId!))}
							</Typography>
						</TableCell>
						{showGross ? (
							<>
								<TableCell align="center">
									{['beforeCalculation', 'onEdit'].includes(status) ? (
										<OutlinedInput
											error={
												checkIsError(row).grossAmount ||
												checkIsError(row).maxValue ||
												checkIsErrorBenefitId(row.serviceId as number)
											}
											isCurrency={true}
											value={formatInputNumberValue(row.grossRetail || '')}
											handleInputNumberChange={formattedValue =>
												handleChangeAmounts(formattedValue, row.serviceId, 'grossRetail')
											}
										/>
									) : (
										<Typography variant="value1">
											{addCurrency(row.grossRetail)}
										</Typography>
									)}
								</TableCell>
								<TableCell align="center" $smallIcon></TableCell>
								<TableCell align="center">
									<Typography variant="value1">
										{addCurrency(row.inStorePromotion)}
									</Typography>
								</TableCell>
								<TableCell align="center" $smallIcon></TableCell>
							</>
						) : null}
						<TableCell align="center">
							{['beforeCalculation', 'onEdit'].includes(status) ? (
								<OutlinedInput
									error={
										checkIsError(row).grossAmount ||
										checkIsError(row).retailNotTaxAndNet ||
										checkIsErrorBenefitId(row.serviceId as number)
									}
									isCurrency={true}
									value={formatInputNumberValue(row.retailAmount || '')}
									handleInputNumberChange={formattedValue =>
										handleChangeAmounts(formattedValue, row.serviceId, 'retailAmount')
									}
								/>
							) : (
								<Typography variant="value1">
									{addCurrency(row.retailAmount)}
								</Typography>
							)}
						</TableCell>
						<TableCell align="center" $smallIcon></TableCell>
						{showTaxColumns ? (
							<>
								<TableCell align="center">
									{['onEdit'].includes(status) ? (
										<OutlinedInput
											error={
												checkIsError(row).retailNotTaxAndNet ||
												checkIsError(row).taxBiggerNet ||
												checkIsErrorBenefitId(row.serviceId as number)
											}
											isCurrency={true}
											value={formatInputNumberValue(row.retailNetOfTax || '0')}
											handleInputNumberChange={formattedValue =>
												handleChangeAmounts(formattedValue, row.serviceId, 'retailNetOfTax')
											}
										/>
									) : (
										<Typography variant="value1">
											{addCurrency(row.retailNetOfTax)}
										</Typography>
									)}
								</TableCell>
								<TableCell align="center" $smallIcon></TableCell>
								<TableCell align="center">
									{['onEdit'].includes(status) ? (
										<OutlinedInput
											error={
												checkIsError(row).retailNotTaxAndNet ||
												checkIsError(row).taxBiggerNet ||
												checkIsErrorBenefitId(row.serviceId as number)
											}
											isCurrency={true}
											value={formatInputNumberValue(row.tax || '0')}
											handleInputNumberChange={formattedValue =>
												handleChangeAmounts(formattedValue, row.serviceId, 'tax')
											}
										/>
									) : (
										<Typography variant="value1">{addCurrency(row.tax)}</Typography>
									)}
								</TableCell>
								<TableCell align="center" $smallIcon></TableCell>
							</>
						) : (
							<></>
						)}
						<TableCell align="center">
							<Typography variant="value1">
								{addCurrency(row.contractualAdjustmentAmount)}
							</Typography>
						</TableCell>
						<TableCell align="center" $smallIcon>
							<Typography variant="value1" style={{ color: palette.grey2.main }}>
								{addCurrency(row.discountedAmount)}
							</Typography>
						</TableCell>
						<TableCell align="center">
							<Typography variant="value1">
								{addCurrency(row.memberPayAmount)}
							</Typography>
						</TableCell>
						<TableCell align="center" $smallIcon></TableCell>
						<TableCell align="center">
							<Typography variant="value1">
								{addCurrency(row.allowablePlanPay)}
							</Typography>
						</TableCell>
						{['beforeCalculation', 'onEdit'].includes(status) &&
						tempAmounts.components.filter(el => isOtherComponent(el)).length > 0 ? (
							<TableCell
								align="center"
								sx={{
									backgroundColor: theme => theme.palette.grey6.main,
									position: 'sticky',
									right: 0,
									width: 44,
								}}
							></TableCell>
						) : null}
					</TableRow>
				))}
		</>
	);
};

export default StandardItemSection;
